import api from './api';

/**
 * Failed-operations centre API.
 *
 * Backs the header button + modal on the dispatch console. Everything the
 * backend could not complete automatically ends up here so it is visible in
 * the app instead of only in a server log.
 */

/** Matches the CHECK constraint in migration 148. */
export type OperationFailureSource =
  | 'container_monitor'
  | 'terminal_session'
  | 'extension_offline'
  | 'booking'
  | 'fix_plate'
  | 'reschedule'
  | 'emodal_link'
  | 'cosco_onhire'
  | 'gate_fee';

export type OperationFailureSeverity = 'warn' | 'error';

export interface OperationFailure {
  id: number;
  source: OperationFailureSource;
  severity: OperationFailureSeverity;
  containerNumber: string | null;
  /** Surrogate key — what the deep link needs to open the detail modal. */
  taskId: number | null;
  /** Business id (e.g. T683010B) — what the dispatcher recognises. */
  taskBusinessId: string | null;
  appointmentId: number | null;
  errorCode: string;
  errorMessage: string | null;
  context: Record<string, unknown>;
  /** How many times this same failure repeated while it stayed open. */
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
}

export interface OperationFailureCounts {
  errors: number;
  warnings: number;
}

interface OperationFailureRow {
  id: number;
  source: OperationFailureSource;
  severity: OperationFailureSeverity;
  container_number: string | null;
  task_id: number | null;
  task_business_id: string | null;
  appointment_id: number | null;
  error_code: string;
  error_message: string | null;
  context: Record<string, unknown> | null;
  occurrence_count: number;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
}

function transformFromBackend(row: OperationFailureRow): OperationFailure {
  return {
    id: row.id,
    source: row.source,
    severity: row.severity,
    containerNumber: row.container_number,
    taskId: row.task_id,
    taskBusinessId: row.task_business_id,
    appointmentId: row.appointment_id,
    errorCode: row.error_code,
    errorMessage: row.error_message,
    context: row.context || {},
    occurrenceCount: row.occurrence_count,
    firstSeenAt: row.first_seen_at,
    lastSeenAt: row.last_seen_at,
    resolvedAt: row.resolved_at,
  };
}

export const operationFailureApi = {
  async list(filters?: {
    status?: 'open' | 'resolved' | 'all';
    source?: OperationFailureSource;
    limit?: number;
    offset?: number;
  }): Promise<{ items: OperationFailure[]; total: number }> {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    const query = params.toString();
    const response = await api.get(`/operation-failures${query ? `?${query}` : ''}`);
    // The interceptor keeps pagination only when BOTH total and limit are
    // present, so this response arrives as { data, total, limit, offset }.
    const payload = response.data as { data?: OperationFailureRow[]; total?: number } | OperationFailureRow[];
    const rows = Array.isArray(payload) ? payload : payload.data || [];
    const total = Array.isArray(payload) ? rows.length : payload.total ?? rows.length;
    return { items: rows.map(transformFromBackend), total };
  },

  async counts(): Promise<OperationFailureCounts> {
    const response = await api.get('/operation-failures/counts');
    return response.data as OperationFailureCounts;
  },

  async resolve(id: number): Promise<void> {
    await api.put(`/operation-failures/${id}/resolve`);
  },

  async resolveAll(source?: OperationFailureSource): Promise<number> {
    const response = await api.put('/operation-failures/resolve-all', source ? { source } : {});
    return (response.data as { resolved: number })?.resolved ?? 0;
  },
};

export default operationFailureApi;
